import { accessCriteria } from '../util/authorization/index';
import { authRoles } from './roleGroups';
import store from '../store';
import { getRawJwt } from '../util/jwt';
import { canAccessTreasurerChecksFromStore } from '../util/shared/treasurerChecksAccess';

const DOCUMENT_TITLE = 'General Ledger';

/**
 * Treasurers Checks can additionally be limited to GL Admins through
 * Settings → GL (`glConfig.restrictTreasurerChecksToGlAdmins`).
 */
const treasurerChecksAccessCriteria = async (to, from, next) => {
  const jwt = getRawJwt();
  if (!jwt) {
    await store.dispatch('reAuth', null, { root: true });
    return;
  }
  const canAccess = await canAccessTreasurerChecksFromStore(store);
  if (!canAccess) {
    next({ name: 'Home' });
    return;
  }
  next();
};

export const GlRoutes = [
  {
    path: '/gl',
    name: 'GeneralLedger',
    meta: {
      title: DOCUMENT_TITLE,
    },
    component: () => import('../views/GeneralLedger.vue'),
    children: [
      {
        path: 'transactions',
        name: 'General Ledger - Transactions',
        meta: {
          title: DOCUMENT_TITLE,
        },
        component: () => import('../views/Gl/Transactions.vue'),
      },
      {
        path: 'browse',
        name: 'General Ledger - Browse',
        meta: {
          title: DOCUMENT_TITLE,
        },
        component: () => import('../views/Gl/browse/browse.vue'),
        children: [
          {
            path: 'headings',
            name: 'General Ledger - Headings & Totals',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/browse/ReportHeadingsTotals.vue'),
          },
          {
            path: 'collateralBank',
            name: 'General Ledger - Collateral Banks',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/browse/CollateralBank.vue'),
          },
        ],
      },
      {
        path: 'sai/transactions',
        name: 'General Ledger - SA&I Transactions',
        meta: {
          title: DOCUMENT_TITLE,
        },
        component: () => import('../views/Gl/sai/Transactions.vue'),
      },
      {
        path: 'utils',
        name: 'General Ledger - Utils',
        meta: {
          title: DOCUMENT_TITLE,
        },
        component: () => import('../views/Gl/utils/Utils.vue'),
        children: [
          {
            path: 'bankReconciliation',
            name: 'General Ledger - Bank Reconciliation',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/BankRecon.vue'),
            children: [
              {
                path: 'reconTransaction',
                name: 'General Ledger - Reconciliation Transaction',
                meta: {
                  title: DOCUMENT_TITLE,
                },
                component: () => import('../../src/components/Gl/BankReconciliation/TransactionList.vue'),
              },
              {
                path: 'reconCodes',
                name: 'General Ledger - Reconciliation Codes',
                meta: {
                  title: DOCUMENT_TITLE,
                },
                component: () => import('../../src/components/Gl/BankCodes/TransactionList.vue'),
              },
            ],
          },
          {
            path: 'calculateInterest',
            name: 'General Ledger - Calculate Interest',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/InterestApportionment.vue'),
          },
          {
            path: 'createfiscalyear',
            name: 'General Ledger - CREATE NEW FISCAL YEAR',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/GeneralLedgerCreateFiscalYear.vue'),
          },
          {
            path: 'checkODBalToGL',
            name: 'General Ledger - CHECK OD BALANCE TO GL',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/CheckODBalanceToGL.vue'),
          },
          {
            path: 'checkMISCBalToGL',
            name: 'General Ledger - BALANCE MISC TO GL',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/CheckMISCBalanceToGL.vue'),
          },
          {
            path: 'checkMTGBalToGL',
            name: 'General Ledger - BALANCE MTG TO GL',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/CheckMTGBalanceToGL.vue'),
          },
          {
            path: 'checkTaxPaymentBalToGL',
            name: 'General Ledger - BALANCE TAX PAYMENT TO GL',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/CheckTaxPaymentBalanceToGL.vue'),
          },
          {
            path: 'checkWarrantBalToGL',
            name: 'General Ledger - BALANCE WARRANT TO GL',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/utils/CheckWarrantBalanceToGL.vue'),
          },
        ],
      },
      {
        path: 'treasurers-checks',
        name: 'General Ledger - Treasurers Checks',
        meta: {
          title: DOCUMENT_TITLE,
        },
        beforeEnter: treasurerChecksAccessCriteria,
        component: () => import('../views/Gl/treasurersChecks/TreasurersChecksUtils.vue'),
        children: [
          {
            path: 'checks',
            name: 'General Ledger - Treasurers Checks - Checks',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/TreasurersChecks.vue'),
          },
          {
            path: 'reports',
            name: 'General Ledger - Treasurers Checks - Reports',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/treasurersChecks/Reports.vue'),
          },
        ],
      },
      {
        path: 'investments',
        name: 'General Ledger - Investment',
        meta: {
          title: DOCUMENT_TITLE,
        },
        component: () => import('../views/Gl/investments/InvestmentUtils.vue'),
        children: [
          {
            path: 'investment',
            name: 'General Ledger - Investments',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/investments/Transactions.vue'),
          },
          {
            path: 'reports',
            name: 'General Ledger - InveReports',
            meta: {
              title: DOCUMENT_TITLE,
            },
            component: () => import('../views/Gl/investments/Reports.vue'),
          },
        ],
      },
    ],
  },
];

GlRoutes.forEach((element, index) => {
  GlRoutes[index].beforeEnter = async (to, from, next) => {
    await accessCriteria(authRoles.GlRead, next);
  };
});
export default GlRoutes;
